import { useState } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import {
  GeoAddressModal,
  GtmNoscript,
  jsonLdScripts,
  LOCAL_CONSENT_SCRIPTS,
  LOCAL_FAVICON_LINKS,
  LOCAL_FONT_LINKS,
  LocalBreadcrumb,
  LocalTopbar,
  PLAN_RIBBONS,
  PlanCard,
  planTermFact,
  RangePanel,
  SEO_LOCAL_CSS,
  SeoLocalIcon,
  ZipCard,
} from 'components/SeoGeoLocal';
import type { GeoModalPlan, PlanCardFact } from 'components/SeoGeoLocal';
import type { AreaView } from 'components/SeoGeoLocal/areaViews';
import {
  formatApproxCount,
  formatSharePct,
  housingMixLabel,
} from 'utils/seoGeo/areaFacts';
import { cartQualifyUrl } from 'utils/seoGeo/cartLink';
import { formatIsoDate, formatPlanPrice } from 'utils/seoGeo/format';
import { localPagesEnabled } from 'utils/seoGeo/pagesEnabled';
import {
  displayPlanName,
  isStreamingPlan,
  planPriceParts,
  planSubtitle,
} from 'utils/seoGeo/planDisplay';
import type { DisplayPlan } from 'utils/seoGeo/planDisplay';

// LUM-578 / ADR-021: ONE reviewed template renders every Xfinity
// neighborhood page across the ten ramp cities plus Philadelphia — pages
// are data, the neighborhood-scale twin of the LUM-492 city route. The
// AREAS allowlist and its validated views live in
// components/SeoGeoLocal/areaViews (LUM-592): this module carries NO data
// imports, so a neighborhood page's browser bundle ships only its own view,
// passed through getStaticProps props. The registry still validates EVERY
// allowlisted entry when the build first imports it, and
// registry-validation.test.ts runs the same guards as a named suite.
//
// Deliberately absent in v1: the FCC coverage section. The committed FCC
// facts are city-level (Census place), so quoting them here would imply a
// neighborhood claim the data does not back. Returns when the per-ZCTA FCC
// fetch is verified.
//
// Existence discipline: siblings pass the anti-doorway gate through the
// ADR-021 content dimension (offers are metro-uniform on purpose; area
// profiles carry the differentiation). An area the gate refuses stays a
// plain-text label on the city page and /local — never force it into the
// registry.

const BBNL_URL = 'https://www.xfinity.com/broadband-labels';
// Brand values follow the XfinitySpecial microsite config
// (src/config/microsites/XfinitySpecial.tsx) — copied as literals so the
// static page doesn't pull the funnel's provider-config import graph into
// its bundle. Keep in sync with the config and the city page.
const HOMEPAGE = 'https://xfinityspecial.com';
const LOGO_SRC = '/provider/themes/XfinityTheme/logo.svg';
const LOGO_ALT = 'Xfinity Authorized Dealer';
const PRIVACY_URL = 'https://updater.com/uhs-terms/';

function planCardFacts(view: AreaView, entry: DisplayPlan): PlanCardFact[] {
  return [
    {
      // One tracked ZIP, so the city page's "N of M ZIP codes" fact reads
      // as noise here. The claim is backed by requireSingleOrderableZip.
      icon: 'check',
      text: `Available in ZIP ${view.zip} in our latest check`,
    },
    planTermFact(entry),
  ];
}

interface AreaLocalPageProps {
  state: string;
  city: string;
  area: string;
  /** ISO date baked at build time, so the static HTML and its hydration
   * can never disagree about the claim dates. */
  pageUpdatedAt: string;
  /** The area's own validated view, built in getStaticProps from its
   * committed data — the ONLY data this page ships (LUM-592). jest suites
   * render this page directly and pass the registry's view explicitly. */
  view: AreaView;
}

export default function AreaLocalPage({
  area,
  pageUpdatedAt,
  view,
}: AreaLocalPageProps) {
  const { snapshot, zip, displayLabel, fullLabel, profile } = view;
  // null = closed; { plan: null } = open without plan context (hero/final CTA).
  const [modal, setModal] = useState<{ plan: GeoModalPlan | null } | null>(
    null
  );
  return (
    <>
      <Head>
        <title>{view.pageTitle}</title>
        <meta name="description" content={view.pageDescription} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={view.canonical} />
        {LOCAL_FAVICON_LINKS}
        {LOCAL_FONT_LINKS}
        {LOCAL_CONSENT_SCRIPTS}
        {/* Machine-readable form of visible content only (LUM-552): the
            breadcrumb trail and the plan cards, same snapshot values the
            page renders. serializeJsonLd escapes "<". */}
        {jsonLdScripts(view.jsonLd)}
      </Head>
      {/* header/main/footer must be SIBLINGS: nested inside <main> they lose
          their banner/contentinfo landmark roles (WCAG 1.3.1). */}
      <div data-testid={`local-${area}-page`} className="seo-local">
        <GtmNoscript />
        {/* React HTML-escapes text children of <style> — SEO_LOCAL_CSS must
            stay free of the escapable characters (regression-tested). */}
        <style>{SEO_LOCAL_CSS}</style>
        <a className="skipLink" href="#geo-main">
          Skip to content
        </a>
        <LocalTopbar
          homeHref={HOMEPAGE}
          logoSrc={LOGO_SRC}
          logoAlt={LOGO_ALT}
          phone={view.phone}
        />

        <main id="geo-main">
          <div className="wrap">
            <LocalBreadcrumb items={view.breadcrumbItems} />

            <div className="hero">
              <span className="eyebrow">
                <SeoLocalIcon name="mapPin" />
                {`${displayLabel}, ${view.cityName} · ZIP ${zip}`}
              </span>
              <h1>{`Xfinity internet in ${displayLabel}, ${view.cityName}`}</h1>
              <p className="lede" data-testid="answer-lead">
                {`Xfinity internet plans in ${displayLabel} currently run from $${view.priceRange.min} to $${view.priceRange.max} a month. In our latest check, plans could be ordered in ZIP ${zip}, the ${displayLabel} ZIP code we track. Your exact price and plan lineup depend on your address.`}
              </p>
              <RangePanel
                headline={`Xfinity is available in ${displayLabel}`}
                stats={[
                  { k: 'Speeds available', v: view.speedRangeLabel },
                  {
                    k: 'Price range',
                    v: (
                      <>
                        {`$${view.priceRange.min} – $${view.priceRange.max}`}
                        <small>/mo*</small>
                      </>
                    ),
                  },
                  { k: 'Plans', v: `${snapshot.plans.length} available` },
                ]}
                primaryCta={{
                  href: view.cartUrl,
                  label: 'Check availability at your address',
                }}
                secondaryCta={{ href: '#plans', label: 'Compare plans below' }}
                note="Offers and prices are not guaranteed and may vary. Check your address to confirm your exact price. No account needed."
                onPrimaryClick={() => setModal({ plan: null })}
              />
            </div>

            <section id="plans">
              <div className="sectionHead">
                <h2>{`Current Xfinity plans in ${displayLabel}`}</h2>
                <p className="sectionSub">
                  {`Current prices for the ${displayLabel} area, verified against the live ordering system.${
                    view.displayPlans.some(
                      (entry) => planPriceParts(entry.plan).from
                    )
                      ? ' Where a plan has two contract terms we show the lowest, marked "from".'
                      : ''
                  } Promotions vary by address, so check yours for the exact price and current offers.`}
                </p>
              </div>
              <div className="plans" data-testid="plans-grid">
                {view.displayPlans.map((entry) => (
                  <PlanCard
                    key={entry.plan.planName}
                    ribbon={entry.ribbon ? PLAN_RIBBONS[entry.ribbon] : null}
                    name={displayPlanName(entry.plan.planName)}
                    subtitle={planSubtitle(entry.plan.planName)}
                    subtitleIcon={
                      isStreamingPlan(entry.plan.planName)
                        ? 'televisionSimple'
                        : 'wifiHigh'
                    }
                    speedMbps={entry.speedMbps}
                    price={planPriceParts(entry.plan)}
                    facts={planCardFacts(view, entry)}
                    bbnl={{ href: BBNL_URL, label: 'See broadband facts' }}
                    cta={{ href: view.cartUrl, label: 'Check availability' }}
                    onCtaClick={() =>
                      setModal({
                        plan: {
                          name: displayPlanName(entry.plan.planName),
                          priceLabel: formatPlanPrice(entry.plan),
                        },
                      })
                    }
                  />
                ))}
              </div>
            </section>

            <section id="coverage">
              <div className="sectionHead">
                <h2>{`Xfinity availability in ${displayLabel}`}</h2>
                <p className="sectionSub">
                  {`Where Xfinity plans could be ordered in our latest check. This page tracks one ZIP code area; the full ${view.cityName} ZIP-by-ZIP picture is linked below.`}
                </p>
              </div>
              <div className="zips">
                <div className="contents" data-testid="orderable-zip">
                  <ZipCard
                    zip={zip}
                    note={`${snapshot.plans.length} plans available`}
                  />
                </div>
              </div>
              <p className="sectionSub" data-testid="city-pointer">
                {`${displayLabel} is one of the ${view.parentTotalZips} ${
                  view.cityName
                } ZIP code areas we track daily.${
                  view.cityGapLabels.length > 0
                    ? ` In our latest check, plans could not be ordered in ${view.cityGapLabels.join(
                        ', '
                      )}.`
                    : ''
                } `}
                {/* Root-relative on purpose, same as the footer links: it
                    resolves on the preview and the apex; dev's /provider
                    basePath misses it (dev-only nuisance). */}
                <a
                  href={`/local/${view.stateSegment}/${view.citySegment}#coverage`}
                  data-testid="city-uplink"
                >
                  {`See the ZIP-by-ZIP picture for all of ${view.cityName}`}
                </a>
                {'.'}
              </p>
            </section>

            <section id="area" data-testid="area-facts">
              <div className="sectionHead">
                {/* Full SP7 label here on purpose: the section describes the
                    ZIP area, and dual-named areas stay accurately named even
                    where the H1 carries an ADR-021 short display label. */}
                <h2>{`About the ${fullLabel} area`}</h2>
                <p className="sectionSub">
                  {`What the Census says about the ZIP ${zip} area. ZIP boundaries don't line up exactly with neighborhood lines, so these figures describe the area around the ZIP. Broadband figures count households with a broadband subscription from any provider. They are not Xfinity coverage figures.`}
                </p>
              </div>
              <div className="factsGrid" data-testid="area-stats">
                <div className="panel bigStat">
                  <div className="v num">
                    {formatApproxCount(profile.population)}
                  </div>
                  <div className="k">{`people live in the ZIP ${zip} area`}</div>
                  <div className="src">
                    {'Source: '}
                    <a href="https://www.census.gov/programs-surveys/acs">
                      {`U.S. Census Bureau, ${view.acsRelease}`}
                    </a>
                    {'. Estimates carry margins of error.'}
                  </div>
                </div>
                <div className="panel">
                  <h3>{`Housing and internet around ZIP ${zip}`}</h3>
                  <p className="panelSub">
                    Shares of homes and workers in this ZIP code area, from the
                    American Community Survey.
                  </p>
                  {[
                    {
                      label: 'Homes in buildings with 5+ units',
                      pct: formatSharePct(profile.apartmentShare),
                    },
                    {
                      label: 'Renter-occupied homes',
                      pct: formatSharePct(profile.renterShare),
                    },
                    {
                      label: 'Households with broadband, any provider',
                      pct: formatSharePct(profile.broadbandShare),
                    },
                    {
                      label: 'Workers who work from home',
                      pct: formatSharePct(profile.workFromHomeShare),
                    },
                  ].map((bar) => (
                    <div className="bar" key={bar.label}>
                      <div className="bl">
                        <span className="bn">{bar.label}</span>
                        <span className="bv num">{bar.pct}</span>
                      </div>
                      <div className="btrack">
                        <span className="bfill" style={{ width: bar.pct }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="sectionSub">
                {/* housingMixLabel shows exactly the share the archetype was
                    derived from (classify-and-display-from-the-same-value). */}
                {`Why it matters here: the housing mix around ZIP ${zip} is ${housingMixLabel(
                  profile
                )}, and availability and pricing are set address by address. In our latest check, all ${
                  snapshot.plans.length
                } plans on this page could be ordered in ZIP ${zip}.`}
              </p>
            </section>

            <section id="check">
              <div className="finalCta">
                <h2>{`See your exact ${displayLabel} offer`}</h2>
                <p>
                  Check your address to see the precise price, promotions, and
                  availability you can order today.
                </p>
                <a
                  className="btn primary"
                  href={view.cartUrl}
                  onClick={(event) => {
                    event.preventDefault();
                    setModal({ plan: null });
                  }}
                >
                  Check availability at your address{' '}
                  <SeoLocalIcon name="arrowRight" />
                </a>
              </div>
            </section>

            {/* Matt's launch protection: the footnote the price asterisks
                point at, kept close to his wording (same as the city page). */}
            <p className="disclaimer" data-testid="price-disclaimer">
              *Offers and prices are not guaranteed and may vary. Search your
              address for the most accurate pricing and offers available at your
              address.
            </p>
          </div>
        </main>

        <footer>
          <div className="wrap foot">
            <div className="legal">
              {`© ${pageUpdatedAt.slice(
                0,
                4
              )} Updater, Inc. All rights reserved `}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </div>
            <p data-testid="provenance">
              {`Plan and price data comes from the live Xfinity ordering system and is re-checked daily; when a check finds a change, we review and update this page. Prices shown are for the ${displayLabel} area (ZIP ${zip}) and vary by address and contract term. Neighborhood area facts: U.S. Census Bureau, ${
                view.acsRelease
              }. Page updated ${formatIsoDate(pageUpdatedAt)}.`}
            </p>
            <div>
              {'Broadband facts and FCC labels: '}
              <a href={BBNL_URL}>xfinity.com/broadband-labels</a>
            </div>
            <div>
              {/* Pages link up, the index links down — the crawl loop that
                  de-orphans the section (LUM-543/LUM-553). */}
              {`All ${view.cityName} ZIP codes: `}
              <a href={`/local/${view.stateSegment}/${view.citySegment}`}>
                {`Xfinity in ${view.cityName}`}
              </a>
            </div>
            <div>
              {'More locations: '}
              <a href="/local">every city we track</a>
            </div>
            <div>
              xfinityspecial.com is an authorized reseller. Xfinity is a
              trademark of Comcast.
            </div>
          </div>
        </footer>

        {modal && (
          <GeoAddressModal
            plan={modal.plan}
            cartUrl={view.cartUrl}
            cartUrlForAddress={(address) =>
              cartQualifyUrl(snapshot.promoId, address)
            }
            cityLabel={displayLabel}
            onClose={() => setModal(null)}
          />
        )}
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Dynamic import on purpose: Next strips getStaticPaths/getStaticProps
  // from browser bundles, so the registry (every neighborhood's data) stays
  // server-side (LUM-592). Imported BEFORE the gate check so the registry's
  // module-scope validation fails ANY build — gated environments included —
  // exactly like the old top-level imports did.
  const { AREAS: registry } = await import('components/SeoGeoLocal/areaViews');
  // Gated environments generate no area pages at all (LUM-501 launch gate).
  if (!localPagesEnabled()) {
    return { paths: [], fallback: false };
  }
  return {
    paths: Object.keys(registry).map((key) => {
      const [state, city, area] = key.split('/');
      return { params: { state, city, area } };
    }),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<AreaLocalPageProps> = async ({
  params,
}) => {
  // Registry first, gate second — validation must not depend on the flag.
  const { AREA_VIEWS } = await import('components/SeoGeoLocal/areaViews');
  if (!localPagesEnabled()) {
    return { notFound: true };
  }
  const state = params?.state;
  const city = params?.city;
  const area = params?.area;
  if (
    typeof state !== 'string' ||
    typeof city !== 'string' ||
    typeof area !== 'string' ||
    !AREA_VIEWS[`${state}/${city}/${area}`]
  ) {
    throw new Error(
      `getStaticProps: "${String(state)}/${String(city)}/${String(
        area
      )}" is not an allowlisted area`
    );
  }
  return {
    props: {
      state,
      city,
      area,
      pageUpdatedAt: new Date().toISOString().slice(0, 10),
      // JSON round-trip strips optional-and-absent fields — Next refuses
      // undefined in props.
      view: JSON.parse(
        JSON.stringify(AREA_VIEWS[`${state}/${city}/${area}`])
      ) as AreaView,
    },
  };
};
